import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, UserPlus, Dice5 } from "lucide-react";

// Roles the Create dialog offers. Higher-tier roles are hidden for lower-tier creators.
const CREATABLE_ROLES = [
  { value: "developer", label: "Developer", creatorRoles: ["developer"] },
  { value: "national_admin", label: "National Admin", creatorRoles: ["developer"] },
  { value: "regional_admin", label: "Regional Admin", creatorRoles: ["developer", "national_admin"] },
  { value: "chapter_leader", label: "Chapter Leader", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "scout_leader", label: "Scout Leader", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "cubs_leader", label: "Cubs Leader", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "patrol_leader", label: "Patrol Leader", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "scout", label: "Scout", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "parent", label: "Parent", creatorRoles: ["developer", "national_admin", "regional_admin"] },
  { value: "consultant", label: "Consultant", creatorRoles: ["developer", "national_admin"] },
];

const NON_MEMBER_ROLES = new Set(["developer", "national_admin", "regional_admin", "consultant", "guest"]);

function suggestPassword() {
  const words = ["Scout", "Eagle", "Mountain", "River", "Compass", "Trail", "Camp", "Fire", "Patrol", "Lantern"];
  const w = words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(100 + Math.random() * 900);
  const sym = "!@#$".charAt(Math.floor(Math.random() * 4));
  return `${w}${n}${sym}`;
}

export default function CreateUserDialog({ open, onClose, onCreated, chapters, regions, currentUser }) {
  const creatorRole = currentUser?.role;
  const availableRoles = useMemo(() => CREATABLE_ROLES.filter(r => r.creatorRoles.includes(creatorRole)), [creatorRole]);
  const defaultChapter = "";

  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "scout",
    chapter_id: defaultChapter, region_id: "", phone: "",
    section: "Scouts", patrol: "", rank: "", position: "Member",
    create_member: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setForm({
      email: "", password: "", name: "", role: "scout",
      chapter_id: defaultChapter, region_id: "", phone: "",
      section: "Scouts", patrol: "", rank: "", position: "Member",
      create_member: true,
    });
    setResult(null);
  };

  const close = () => { reset(); onClose?.(); };

  const generate = () => setForm(f => ({ ...f, password: suggestPassword() }));

  const copyPassword = async () => {
    try { await navigator.clipboard.writeText(form.password); toast.success("Password copied"); }
    catch { toast("Copy failed — select and copy manually"); }
  };

  const submit = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("Email, name and password are required"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    const payload = { ...form };
    if (NON_MEMBER_ROLES.has(form.role)) payload.create_member = false;
    if (form.role === "regional_admin" && !form.region_id) { toast.error("Pick a region for the regional admin"); return; }
    if (creatorRole === "regional_admin" && !form.chapter_id) { toast.error("Pick a chapter in your region"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", payload);
      setResult(data);
      toast.success(`Created ${data.user?.email}`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const needsMemberFields = !NON_MEMBER_ROLES.has(form.role);
  const canPickChapter = true;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus size={18}/> Create a user account</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="create-user-success">
            <div className="rounded-xl bg-[hsl(149,40%,30%)]/10 border border-[hsl(149,40%,30%)]/40 p-4">
              <div className="font-bold text-[hsl(149,40%,25%)] mb-1">Account created ✓</div>
              <div className="text-sm">
                <b>{result.user?.name}</b> &lt;{result.user?.email}&gt; · role: <code>{result.user?.role}</code>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                They can log in with the password you typed. They'll be prompted to change it on first login.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="btn-pill" onClick={reset}>Create another</Button>
              <Button className="btn-pill bg-[hsl(149,40%,30%)]" onClick={close} data-testid="create-user-done">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Narek Arzoumanian" data-testid="cu-name"/>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="scout@example.com" data-testid="cu-email"/>
              </div>
              <div>
                <Label>Phone <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+374…" data-testid="cu-phone"/>
              </div>

              <div className="col-span-2">
                <Label>Password</Label>
                <div className="flex gap-2">
                  <PasswordInput value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="At least 8 characters" data-testid="cu-password"/>
                  <Button type="button" variant="outline" className="btn-pill flex-shrink-0" onClick={generate} data-testid="cu-generate" title="Generate a password"><Dice5 size={14}/></Button>
                  <Button type="button" variant="outline" className="btn-pill flex-shrink-0" disabled={!form.password} onClick={copyPassword} data-testid="cu-copy" title="Copy password"><Copy size={14}/></Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">The new user will be prompted to change it on their first login.</p>
              </div>

              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="cu-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Chapter{needsMemberFields ? " *" : ""}</Label>
                <Select value={form.chapter_id || "__none__"} onValueChange={v => setForm({...form, chapter_id: v === "__none__" ? "" : v})} disabled={!canPickChapter}>
                  <SelectTrigger data-testid="cu-chapter"><SelectValue placeholder="Select chapter"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(chapters || []).map(c => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.name}{c.short_name ? ` (${c.short_name})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {creatorRole === "regional_admin" && <p className="text-[11px] text-muted-foreground mt-1">Only chapters in your region.</p>}
              </div>

              {form.role === "regional_admin" && (
                <div className="col-span-2">
                  <Label>Region *</Label>
                  <Select value={form.region_id || "__none__"} onValueChange={v => setForm({...form, region_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="cu-region"><SelectValue placeholder="Select region"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {(regions || []).map(r => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {needsMemberFields && (
                <>
                  <div>
                    <Label>Section</Label>
                    <Select value={form.section} onValueChange={v => setForm({...form, section: v})}>
                      <SelectTrigger data-testid="cu-section"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["Cubs","Scouts","Senior Scouts","Rovers"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Patrol <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                    <Input value={form.patrol} onChange={e => setForm({...form, patrol: e.target.value})} data-testid="cu-patrol"/>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={close} disabled={submitting} data-testid="cu-cancel">Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="btn-pill bg-[hsl(149,40%,30%)]" data-testid="cu-submit">
                <UserPlus size={14} className="mr-2"/>
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
